import { Logger } from '@quickplay/foundation';
import { FrameType, MetadataInformation, MetaTag } from '@quickplay/player-interface';
import { YospacePlayer } from '../YospacePlayer';
import { LiveSessionHandler } from './LiveSessionHandler';

/**
 * The purpose of this module is to re-publish `timedmetadata` events as the playhead
 * advances. Shaka (and perhaps Safari player) publish events as they emsg/id3 boxes
 * get appended into the buffer, and synchronously publish these events to the application.
 * This means that the app receives these events before the playhead has arrived at
 * their point in the stream. Ad vendor SDK's (specifically, YoSpace), require that
 * we publish these events as the playhead passes the `startTime` of the event.
 */
export class TimedMetadataHandler {
  player: YospacePlayer;
  session: any;
  yospaceSDK: any;
  sessionHandler: LiveSessionHandler;
  logger: Logger;

  private queue: any[] = [];

  constructor(player: YospacePlayer, session: any, yospaceSDK: any, logger: Logger) {
    this.player = player;
    this.session = session;
    this.yospaceSDK = yospaceSDK;
    this.sessionHandler = new LiveSessionHandler(yospaceSDK, logger);
    this.logger = logger;
  }

  onSeek = (): void => {
    // Flush the queue, because:
    //   A) If a user has seeked backward, it's possible there are future emsg events still
    //      in the queue, and we would now publish them in duplicate.
    //   B) If a user has seeked forward, it's possible there will be very old emsg events
    //      in the queue.
    this.logger.log('Flushing the timedmetadata queue');
    this.queue = [];
  };

  onProgress = (currentTime: number): void => {
    const currentEvents = this.findCurrentEvents();
    currentEvents.forEach(event => this.publishEvent(event, currentTime));
  };

  enqueue = (event: any): void => {
    this.queue.push(event);
  };

  dequeue = (): void => {
    return this.queue.shift();
  };

  private publishEvent = (event: any, currentTime: number): void => {
    let timedMetadata = undefined;
    if (event.type === 'emsg') {
      // extract meta tag data from event
      const metadataInformation = new MetadataInformation([this.buildMetaTag(event)]);
      timedMetadata = this.sessionHandler.createTimedMetadataFromEmsgEvent(metadataInformation, currentTime);
    } else if (event.type === 'metadata') {
      timedMetadata = this.sessionHandler.createTimedMetadataFromMetadataEvent(event, currentTime);
    }

    if (timedMetadata !== null) {
      this.session.onTimedMetadata(timedMetadata);
    }
  };

  private next = (): any => {
    return this.queue[0];
  };

  private getPlayheadPosition = (): number => {
    return this.player.currentTime;
  };

  private findCurrentEvents = (): any[] => {
    // Potentially there are multiple events which have passed since the last `timeupdate`,
    // so this function returns an array.
    const currentEvents = [];
    let nextEvent = this.next();

    while (nextEvent && nextEvent.startTime <= this.getPlayheadPosition()) {
      currentEvents.push(this.dequeue());
      nextEvent = this.next();
    }

    return currentEvents;
  };

  private buildMetaTag = (event: any): MetaTag => {
    return {
      type: FrameType.META_TAG_EMSG_TYPE,
      id: event.id,
      extendedAttributes: event.messageData,
      schemeIdUri: event.schemeIdUri,
    };
  };
}
